export class GlobalListeners {
	constructor() {
		this.menuCloseTimer = null; // Timer for delayed menu closing
		this.selectors = {
			productForm: `[action="${window.Shopify.routes.root}cart/add"]`,
			removeButtons: '.cart .items-wrapper .cart-item .remove-item',
			quantitySelectorPlus: '.cart .items-wrapper .cart-item .quantity-wrapper .plus',
			quantitySelectorMinus: '.cart .items-wrapper .cart-item .quantity-wrapper .minus',
			accordion: 'accordion',
			footerColumnTitles: '.site-footer .footer-columns .footer-column p',
			benefitsSection: '.benefits',
			benefitTab: '.benefits .tab',
			benefit: '.benefits .benefit',
			benefitContent: '.benefits .content',
			resultsSection: '.results .column',
			resultsTab: '.results .tab',
			resultsImg: '.results .media-img',
			blogGridSection: '.article-grid-section',
			blogGridTab: '.article-grid-section .tab',
			accountSection: '.account-dashboard',
			accountTab: '.account-dashboard .tab',
			productCard: '.product-card',
			productCardSwiper: '.product-card .swiper-container',
			quickViewCard: '[open-quick-view-card]',
			quickviewCardModal: '.quick-view-card-container',
			quickviewCardClose: '.quick-view-card-container .close',
			quickviewCardInner: '.quick-view-card-container .inner',
			productTitleBadges: '.product-title-badges',
			animateElements: '[qf-animate]'
		};

		this.init();
	}

	init() {
		// Use console.log instead of window.log for safety
		if (window.log) {
			window.log("Initializing global QF listeners");
		} else {
			// console.log("Initializing global QF listeners");
		}
		document.body.addEventListener('click', this.handleEvent.bind(this));
		document.body.addEventListener('submit', this.handleFormSubmit.bind(this));
		document.body.addEventListener('mouseover', this.handleMenuHover.bind(this));
		document.body.addEventListener('mouseenter', this.handleTopLevelMenuHover.bind(this), true);
		document.body.addEventListener('mouseleave', this.handleTopLevelMenuLeave.bind(this), true);
		this.initBenefitSwipers();
		this.initResultsTabs();
		this.initBlogGridTabs();
		this.initAccountTabs();
		this.handleAnimations();
		this.initQuickViewCardListeners();
		this.initProductCardSwipers();
		this.initScrollListener();
		this.handleProductTitleBadges();
	}

	initScrollListener() {
		window.addEventListener('scroll', this.handleScroll.bind(this));
	}

	handleScroll() {
		const scrollPosition = window.scrollY || document.documentElement.scrollTop;
		const footer = document.querySelector('.site-footer');
		const stickWidget = document.querySelector('.fixed-stick-widget');

		if (!footer || !stickWidget) return;

		const footerRect = footer.getBoundingClientRect();
		const widgetRect = stickWidget.getBoundingClientRect();

		// Check if the stick widget is about to overlap the footer
		if (footerRect.top < window.innerHeight) {
			// Footer is in view, adjust position of the stick widget
			stickWidget.style.position = 'absolute';
			stickWidget.style.top = `${window.scrollY + footerRect.top - widgetRect.height - 15}px`;
		} else {
			// Footer is not in view, keep the stick widget fixed
			stickWidget.style.position = 'fixed';
			stickWidget.style.top = 'calc(100vh - 85px)';
		}

		// Add/remove 'scrolled' class based on the scroll position
		if (scrollPosition > 30) {
			document.body.classList.add('scrolled');
		} else {
			document.body.classList.remove('scrolled');
		}
	}

	handleTopLevelMenuHover(e) {
		const topLevelLink = e.target.closest('.top-level-link.has-children');
		const megaMenu = e.target.closest('.mega-menu-dropdown') || e.target.closest('.mega-menu-dropdown_outer-wrapper');

		// Clear any pending close timer if we're entering a relevant element
		if (this.menuCloseTimer && (topLevelLink || megaMenu)) {
			clearTimeout(this.menuCloseTimer);
			this.menuCloseTimer = null;
		}

		// Only handle hover on non-hamburger top-level links
		if (topLevelLink && !topLevelLink.classList.contains('hamburger-menu-item')) {
			const id = topLevelLink.getAttribute('data-menu-id');
			if (id) {
				const associatedMegaMenu = document.querySelector(`.mega-menu-dropdown[data-menu-id="${id}"]`);
				const megaMenuOverlay = document.querySelector(`.mega-menu-overlay`);
				if (associatedMegaMenu) {
					// Close all mega menus first
					document.querySelectorAll('.mega-menu-dropdown.active').forEach((menu) => {
						menu.classList.remove('active');
					});
					document.querySelectorAll('.top-level-link.active').forEach((link) => {
						link.classList.remove('active');
					});

					// Open the hovered menu
					topLevelLink.classList.add('active');
					associatedMegaMenu.classList.add('active');
	
					// Show first menu item blocks by default
					const firstMenuItem = associatedMegaMenu.querySelector('[data-menu-item]');
					if (firstMenuItem) {
						this.showBlocksForMenuItem(firstMenuItem);
					}
	
					// Show overlay
					if (megaMenuOverlay) {
						megaMenuOverlay.classList.add('active');
					}
				}
			}
		}
	}

	handleTopLevelMenuLeave(e) {
		// Check if we're leaving a top-level link or mega menu dropdown
		const topLevelLink = e.target.closest('.top-level-link.has-children');
		const megaMenuDropdown = e.target.closest('.mega-menu-dropdown') || e.target.closest('.mega-menu-dropdown_outer-wrapper');

		if (topLevelLink && !topLevelLink.classList.contains('hamburger-menu-item')) {
			// Don't close if moving to the dropdown
			const relatedTarget = e.relatedTarget;
			if (relatedTarget) {
				// Don't close if staying within the same top-level link
				if (topLevelLink.contains(relatedTarget)) {
					return;
				}
				// Don't close if moving to the dropdown
				if (relatedTarget.closest('.mega-menu-dropdown') || relatedTarget.closest('.mega-menu-dropdown_outer-wrapper')) {
					return;
				}
			}

			const rect = topLevelLink.getBoundingClientRect();
			const delay = (e.clientY >= rect.bottom) ? 500 : 100;

			// Use a delay before closing to give time to move to dropdown
			this.menuCloseTimer = setTimeout(() => {
				const id = topLevelLink.getAttribute('data-menu-id');
				if (id) {
					const associatedMegaMenu = document.querySelector(`.mega-menu-dropdown[data-menu-id="${id}"]`);
					if (associatedMegaMenu) {
						topLevelLink.classList.remove('active');
						associatedMegaMenu.classList.remove('active');

						// Hide overlay if no menus are open
						const megaMenuOverlay = document.querySelector(`.mega-menu-overlay`);
						if (megaMenuOverlay) {
							const anyMenuOpen = document.querySelector('.mega-menu-dropdown.active');
							if (!anyMenuOpen) {
								megaMenuOverlay.classList.remove('active');
							}
						}
					}
				}
			}, delay);
		}

		if (megaMenuDropdown) {
			// Don't close if moving to the top-level link
			const relatedTarget = e.relatedTarget;
			if (relatedTarget) {
				// Don't close if staying within the same mega menu dropdown
				if (megaMenuDropdown.contains(relatedTarget)) {
					return;
				}
				// Don't close if moving to the top-level link
				if (relatedTarget.closest('.top-level-link.has-children')) {
					return;
				}
			}

			const rect = megaMenuDropdown.getBoundingClientRect();
			const delay = (e.clientY <= rect.top) ? 500 : 100;

			// Use a delay before closing
			this.menuCloseTimer = setTimeout(() => {
				const dropdown = megaMenuDropdown.classList.contains('mega-menu-dropdown') ? megaMenuDropdown : megaMenuDropdown.querySelector('.mega-menu-dropdown');
				if (dropdown) {
					const id = dropdown.getAttribute('data-menu-id');
					if (id) {
						const associatedLink = document.querySelector(`.top-level-link[data-menu-id="${id}"]`);
						if (associatedLink && !associatedLink.classList.contains('hamburger-menu-item')) {
							dropdown.classList.remove('active');
							associatedLink.classList.remove('active');

							// Hide overlay if no menus are open
							const megaMenuOverlay = document.querySelector(`.mega-menu-overlay`);
							if (megaMenuOverlay) {
								const anyMenuOpen = document.querySelector('.mega-menu-dropdown.active');
								if (!anyMenuOpen) {
									megaMenuOverlay.classList.remove('active');
								}
							}
						}
					}
				}
			}, delay);
		}
	}

	handleMenuHover(e) {
		// Only handle hover on menu items inside the dropdown
		if (!e.target.closest('.sublinks-container')) return;

		// Look for any element with data-menu-item attribute
		const menuItem = e.target.closest('[data-menu-item]');
		if (menuItem) {
			this.showBlocksForMenuItem(menuItem);
		}
	}

	handleFormSubmit(e) {
		this.productForm(e);
	}

	/* Benefits block */
	initializeActiveBenefitSwiper(section) {
		const activeBenefit = section.querySelector('.benefit.active');
		if (activeBenefit) {
			const swiperElement = activeBenefit.querySelector('.swiper');
			if (swiperElement) {
				const blockId = swiperElement.getAttribute('id');
				// console.log('blockId', blockId);
				const mobileView = section.dataset.slidersPerViewMobile;
				const tabletView = section.dataset.slidersPerViewTablet;
				const desktopView = section.dataset.slidersPerViewDesktop;
				const slideGap = section.dataset.slideGap;

				this.initSwiper(blockId, mobileView, tabletView, desktopView, slideGap);
			}
		}
	}

	initSwiper(blockId, mobileView, tabletView, desktopView, slideGap) {
		if (!blockId) {
			return;
		}
		const swiperInstance = new window.swiper(`#${blockId}.benefit-product-swiper`, {
			slidesPerView: mobileView,
			spaceBetween: slideGap,
			slidesOffsetBefore: slideGap,
			slidesOffsetAfter: slideGap,
			loop: true,
			lazy: {
				enabled: true,
				checkInView: true,
				loadOnTransitionStart: true,
				loadPrevNext: true,
				loadPrevNextAmount: 1
			},
			breakpoints: {
				992: {
					slidesPerView: tabletView,
					slidesOffsetBefore: 0,
					slidesOffsetAfter: 0,
					loop: false,
				},
				1025: {
					slidesPerView: desktopView,
					slidesOffsetBefore: 0,
					slidesOffsetAfter: 0,
					loop: false,
				}
			}
		});

		return swiperInstance;
	}

	initBenefitSwipers() {
		const benefitSections = document.querySelectorAll(this.selectors.benefitsSection);

		benefitSections.forEach(section => {
			this.initializeActiveBenefitSwiper(section);

			const tabs = section.querySelectorAll(this.selectors.benefitTab);
			tabs.forEach(tab => {
				tab.addEventListener('click', () => {
					this.handleTabClick(tab, section);
				});
			});

			this.updateTabsPosition(section);
			this.updateActiveTabBackground(section);
			this.initializeVideoObserver(section);
			this.initHammerListeners(section);
		});
	}

	handleTabClick(tab, section) {
		const dataId = tab.getAttribute('data-id');
		const tabs = section.querySelectorAll(this.selectors.benefitTab);
		const benefits = section.querySelectorAll(this.selectors.benefit);

		tabs.forEach(tab => tab.classList.remove('active'));
		benefits.forEach(benefit => benefit.classList.remove('active'));

		tab.classList.add('active');
		benefits.forEach(benefit => {
			if (benefit.getAttribute('data-id') === dataId) {
				benefit.classList.add('active');
				const swiperElement = benefit.querySelector('.swiper');
				if (swiperElement) {
					const blockId = swiperElement.getAttribute('id');
					const mobileView = section.dataset.slidersPerViewMobile;
					const tabletView = section.dataset.slidersPerViewTablet;
					const desktopView = section.dataset.slidersPerViewDesktop;
					const slideGap = section.dataset.slideGap;

					this.initSwiper(blockId, mobileView, tabletView, desktopView, slideGap);
				}
			}
		});

		this.updateActiveTabBackground(section);
	}

	updateTabsPosition(section) {
		const activeBenefit = section.querySelector('.benefit.active');
		if (activeBenefit) {
			const media = activeBenefit.querySelector('.media');
			const tabsContainer = section.querySelector('.tabs');

			if (tabsContainer && media) {
				function splitTabsIntoRows() {
					const tabRow = tabsContainer.querySelector('.tab-row');
					const tabs = Array.from(tabRow.querySelectorAll('.tab'));
					const tabCount = tabs.length;
					const middleIndex = Math.ceil(tabCount / 2);

					const firstRow = document.createElement('div');
					firstRow.classList.add('tab-row');

					const secondRow = document.createElement('div');
					secondRow.classList.add('tab-row');

					tabs.forEach((tab, index) => {
						if (index < middleIndex) {
							firstRow.appendChild(tab);
						} else {
							secondRow.appendChild(tab);
						}
					});

					// Remove the original tab row and append the new rows
					tabRow.remove();
					tabsContainer.appendChild(firstRow);
					tabsContainer.appendChild(secondRow);

					// Add the tabs-mobile class
					tabsContainer.classList.add('tabs-mobile');
				}

				// Initial setup
				// if (window.innerWidth < 768 && tabsContainer.offsetWidth > 300) {
				//     splitTabsIntoRows();
				// }

				// Recalculate the tabsContainer height after splitting
				const mediaHeight = media.offsetHeight;
				const tabsHeight = tabsContainer.offsetHeight;
				const remainingHeight = mediaHeight - tabsHeight;

				tabsContainer.style.top = `${remainingHeight - 20}px`;
			}
		}
	}

	updateActiveTabBackground(section) {
		const tabsContainer = section.querySelector('.tabs');
		const activeTab = tabsContainer.querySelector('.tab.active');

		if (tabsContainer && activeTab) {
			const tabRow = activeTab.closest('.tab-row');
			const tabsContainerLeft = tabsContainer.getBoundingClientRect().left;
			const tabRowLeft = tabRow.getBoundingClientRect().left;
			const activeLeft = activeTab.offsetLeft;
			const activeTop = tabRow.offsetTop + activeTab.offsetTop;
			const activeHeight = activeTab.offsetHeight;
			const activeWidth = activeTab.offsetWidth;

			tabsContainer.style.setProperty('--active-tab-left', `${activeLeft + tabRowLeft - tabsContainerLeft}px`);
			tabsContainer.style.setProperty('--active-tab-top', `${activeTop}px`);
			tabsContainer.style.setProperty('--active-tab-width', `${activeWidth}px`);
			tabsContainer.style.setProperty('--active-tab-height', `${activeHeight}px`);
		}
	}

	initResultsTabs() {
		const resultsSections = document.querySelectorAll(this.selectors.resultsSection);

		resultsSections.forEach(section => {
			const tabs = section.querySelectorAll(this.selectors.resultsTab);
			tabs.forEach(tab => {
				tab.addEventListener('click', () => {
					this.handleResultsTabClick(tab, section);
				});
			});

			this.updateActiveTabBackground(section);
		});
	}

	handleResultsTabClick(tab, section) {
		const dataId = tab.getAttribute('data-id');
		const tabs = section.querySelectorAll(this.selectors.resultsTab);
		const resultsImgs = section.querySelectorAll(this.selectors.resultsImg);

		tabs.forEach(tab => tab.classList.remove('active'));
		resultsImgs.forEach(image => image.classList.remove('active'));

		tab.classList.add('active');
		resultsImgs.forEach(image => {
			if (image.getAttribute('data-id') === dataId) {
				image.classList.add('active');
			}
		});

		this.updateActiveTabBackground(section);
	}

	initBlogGridTabs() {
		const blogGridSections = document.querySelectorAll(this.selectors.blogGridSection);

		blogGridSections.forEach(section => {
			const tabs = section.querySelectorAll(this.selectors.blogGridTab);
			tabs.forEach(tab => {
				tab.addEventListener('click', () => {
					this.handleBlogGridTabClick(tab, section);
				});
			});

			this.updateBlogGridTabsPosition(section);
			this.updateActiveTabBackground(section);
		});
	}

	handleBlogGridTabClick(tab, section) {
		const tabs = section.querySelectorAll(this.selectors.blogGridTab);
		tabs.forEach(tab => tab.classList.remove('active'));
		tab.classList.add('active');
		this.updateActiveTabBackground(section);
	}

	updateBlogGridTabsPosition(section) {
		//const media = activeBenefit.querySelector('.media');
		const tabsContainer = section.querySelector('.tabs');

		if (tabsContainer) {
			function splitTabsIntoRows() {
				const tabRow = tabsContainer.querySelector('.tab-row');
				const tabs = Array.from(tabRow.querySelectorAll('.tab'));
				const tabCount = tabs.length;
				const middleIndex = Math.ceil(tabCount / 2);

				const firstRow = document.createElement('div');
				firstRow.classList.add('tab-row');

				const secondRow = document.createElement('div');
				secondRow.classList.add('tab-row');

				tabs.forEach((tab, index) => {
					if (index < middleIndex) {
						firstRow.appendChild(tab);
					} else {
						secondRow.appendChild(tab);
					}
				});

				// Remove the original tab row and append the new rows
				tabRow.remove();
				tabsContainer.appendChild(firstRow);
				tabsContainer.appendChild(secondRow);

				// Add the tabs-mobile class
				tabsContainer.classList.add('tabs-mobile');
			}

			// Initial setup
			if (window.innerWidth < 768 && tabsContainer.offsetWidth > 300) {
				splitTabsIntoRows();
			}
		}
	}

	initAccountTabs() {
		const accountSections = document.querySelectorAll(this.selectors.accountSection);

		accountSections.forEach(section => {
			const tabs = section.querySelectorAll(this.selectors.accountTab);
			tabs.forEach(tab => {
				tab.addEventListener('click', () => {
					this.handleAccountTabClick(tab, section);
				});
			});

			this.updateAccountTabsPosition(section);
			this.updateActiveTabBackground(section);
		});
	}

	handleAccountTabClick(tab, section) {
		const tabs = section.querySelectorAll(this.selectors.accountTab);
		tabs.forEach(tab => tab.classList.remove('active'));
		tab.classList.add('active');
		this.updateActiveTabBackground(section);
	}

	updateAccountTabsPosition(section) {
		const tabsContainer = section.querySelector('.tabs');

		if (tabsContainer) {
			function splitTabsIntoRows() {
				const tabRow = tabsContainer.querySelector('.tab-row');
				const tabs = Array.from(tabRow.querySelectorAll('.tab'));
				const tabCount = tabs.length;
				const middleIndex = Math.ceil(tabCount / 2);

				const firstRow = document.createElement('div');
				firstRow.classList.add('tab-row');

				const secondRow = document.createElement('div');
				secondRow.classList.add('tab-row');

				tabs.forEach((tab, index) => {
					if (index < middleIndex) {
						firstRow.appendChild(tab);
					} else {
						secondRow.appendChild(tab);
					}
				});

				// Remove the original tab row and append the new rows
				tabRow.remove();
				tabsContainer.appendChild(firstRow);
				tabsContainer.appendChild(secondRow);

				// Add the tabs-mobile class
				tabsContainer.classList.add('tabs-mobile');
			}

			// Initial setup
			if (window.innerWidth < 768 && tabsContainer.offsetWidth > 300) {
				splitTabsIntoRows();
			}
		}
	}

	initializeVideoObserver(section) {
		const benefits = section.querySelectorAll('.benefit');
		const observer = new IntersectionObserver(entries => {
			entries.forEach(entry => {
				// Only handle videos in active benefits
				if (!entry.target.classList.contains('active')) {
					return;
				}

				const video = entry.target.querySelector('video');
				if (video) {
					// Get the element's position
					const rect = entry.target.getBoundingClientRect();
					const isVisible = rect.top < window.innerHeight && rect.bottom > 0;
					const isAtTopOfPage = rect.top <= 0 && rect.bottom > 0;

					// Play video if it's visible in viewport (either intersecting or at top of page)
					if (entry.isIntersecting || (isVisible && isAtTopOfPage)) {
						// Only play if not already playing
						if (video.paused) {
							video.play().catch(error => {
								console.error('Video play interrupted', error);
							});
						}
					} else {
						// Only pause if completely out of viewport and not already paused
						if (!isVisible && !video.paused) {
							video.pause();
						}
					}
				}
			});
		}, {
			threshold: [0, 0.25, 0.5, 0.75, 1],
			rootMargin: '0px'
		});

		benefits.forEach(benefit => {
			observer.observe(benefit);

			// Initial check: play video immediately if active benefit is already in viewport on page load
			if (benefit.classList.contains('active')) {
				const video = benefit.querySelector('video');
				if (video) {
					const rect = benefit.getBoundingClientRect();
					const isVisible = rect.top < window.innerHeight && rect.bottom > 0;
					if (isVisible && video.paused) {
						video.play().catch(error => {
							console.error('Video play interrupted', error);
						});
					}
				}
			}
		});
	}

	initHammerListeners(section) {
		const benefitMedias = section.querySelectorAll('.media');

		benefitMedias.forEach(media => {
			const hammer = new window.hammer(media);

			hammer.on('swipeleft', () => {
				this.activateNextBenefit(section);
			});

			hammer.on('swiperight', () => {
				this.activatePreviousBenefit(section);
			});
		});
	}

	activateNextBenefit(section) {
		const activeBenefit = section.querySelector('.benefit.active');
		const nextBenefit = activeBenefit.nextElementSibling;

		if (nextBenefit && nextBenefit.classList.contains('benefit')) {
			const nextIndex = nextBenefit.getAttribute('data-id');
			this.activateBenefitTab(section, nextIndex);
		}
	}

	activatePreviousBenefit(section) {
		const activeBenefit = section.querySelector('.benefit.active');
		const previousBenefit = activeBenefit.previousElementSibling;

		if (previousBenefit && previousBenefit.classList.contains('benefit')) {
			const prevIndex = previousBenefit.getAttribute('data-id');
			this.activateBenefitTab(section, prevIndex);
		}
	}

	activateBenefitTab(section, index) {
		const tabs = section.querySelectorAll(this.selectors.benefitTab);
		const benefits = section.querySelectorAll(this.selectors.benefit);

		// Remove active class from all tabs and benefits
		tabs.forEach(tab => tab.classList.remove('active'));
		benefits.forEach(benefit => benefit.classList.remove('active'));

		// Activate the tab and benefit corresponding to the new index
		const activeTab = section.querySelector(`.tab[data-id="${index}"]`);
		const activeBenefit = section.querySelector(`.benefit[data-id="${index}"]`);

		if (activeTab) activeTab.classList.add('active');
		if (activeBenefit) activeBenefit.classList.add('active');

		// Initialize swiper inside the active benefit
		const swiperElement = activeBenefit.querySelector('.swiper');
		if (swiperElement) {
			const blockId = swiperElement.getAttribute('id');
			const mobileView = section.dataset.slidersPerViewMobile;
			const tabletView = section.dataset.slidersPerViewTablet;
			const desktopView = section.dataset.slidersPerViewDesktop;
			const slideGap = section.dataset.slideGap;

			this.initSwiper(blockId, mobileView, tabletView, desktopView, slideGap);
		}

		this.updateActiveTabBackground(section);
	}
	/* END Benefits block */

	handleEvent(e) {
		this.megaMenu(e);
		this.mobileSubmenu(e);
		this.headerSearch(e);
		this.quantityButtons(e);
		this.removeCartItem(e);
		this.accordions(e);
		this.mobileMenu(e);
		this.footerMobile(e);
	}

	productForm(e) {
		const productForm = e.target.closest(this.selectors.productForm);
		if (productForm) {
			// Skip if this form is already handled by the product-form custom element
			if (productForm.closest('product-form')) {
				return;
			}

			e.preventDefault();

			const quantity_el = productForm.querySelector(".quantity-select input[type='number']");
			let quantity = 1;
			if (quantity_el) {
				quantity = parseInt(quantity_el.value);
			}

			// Dynamically find the select element by its name (or you can use the dynamic ID if you have one)
			const idInput = productForm.querySelector("select[name='id']") || productForm.querySelector("input[name='id']");

			if (idInput) {
				const id = idInput.value;

				let properties = {};
				const propertyFields = productForm.querySelectorAll('.property');

				if (propertyFields) {
					propertyFields.forEach(field => {
						const key = field.getAttribute('name');
						const value = field.value;
						if (key && value) {
							properties[key] = value;
						}
					});
				}

				if (quantity && id) {
					window.cart.add({ items: [{ "id": id, "quantity": quantity, "properties": properties }] });
				} else {
					console.error("Error: Missing quantity or product ID.");
				}
			} else {
				console.error("Error: Product ID select element not found.");
			}
		}
	}

	handleAnimations() {
		const elements = document.querySelectorAll(this.selectors.animateElements);

		const observerOptions = {
			root: null,
			rootMargin: '0px',
			threshold: 0.1
		};

		const observer = new IntersectionObserver((entries, observer) => {
			entries.forEach(entry => {
				if (entry.isIntersecting) {
					entry.target.classList.add('in-view');
					observer.unobserve(entry.target);
				}
			});
		}, observerOptions);

		elements.forEach(element => {
			observer.observe(element);
		});
	}

	quantityButtons(e) {
		// QUANTITY PLUS CART
		const quantityPlus = e.target.closest(this.selectors.quantitySelectorPlus);
		if (quantityPlus) {
			if (quantityPlus.getAttribute("data-pending") === "true") return;
			quantityPlus.setAttribute("data-pending", true);

			const quantityElement = quantityPlus.parentNode.querySelector(".quantity span");
			const stock = quantityElement.getAttribute("data-stock");
			const current = parseInt(quantityElement.innerHTML);
			const variant_key = quantityElement.closest('.cart-item').getAttribute("data-variant-key");
			const inv_policy = quantityElement.getAttribute("data-inventory-policy");
			if (inv_policy === "continue" || stock > 0) {
				// if in stock or continue selling when out of stock, update cart
				const updated = current + 1;
				window.cart.change({ "id": variant_key, "quantity": updated });
			}
		}

		// QUANTITY MINUS CART
		const quantityMinus = e.target.closest(this.selectors.quantitySelectorMinus);
		if (quantityMinus) {
			if (quantityMinus.getAttribute("data-pending") === "true") return;
			quantityMinus.setAttribute("data-pending", true);

			const quantity = quantityMinus.parentNode.querySelector(".quantity-wrapper span");
			const current = parseInt(quantity.innerHTML);
			const variant_key = quantity.closest('.cart-item').getAttribute("data-variant-key");

			if (current > 0) {
				const updated = current - 1;
				window.cart.change({ "id": variant_key, "quantity": updated });
			}
		}
	}

	removeCartItem(e) {
		// REMOVE ITEM CART
		const removeButtons = e.target.closest(this.selectors.removeButtons);
		if (removeButtons) {
			const variant_key = removeButtons.closest('.cart-item').getAttribute("data-variant-key");
			window.cart.change({ "id": variant_key, "quantity": 0 });
		}
	}

	megaMenu(e) {
		const target = e.target;

		if (target.closest('.close-mega-menu')) {
			e.stopPropagation();
			const megaMenuDropdown = target.closest('.mega-menu-dropdown');

			if (megaMenuDropdown) {
				megaMenuDropdown.classList.remove('active');
				const associatedLink = document.querySelector(`.top-level-link[data-menu-id="${megaMenuDropdown.getAttribute('data-menu-id')}"]`);
				const megaMenuOverlay = document.querySelector(`.mega-menu-overlay`);
				if (associatedLink) {
					associatedLink.classList.remove('active');

					if (megaMenuOverlay) {
						megaMenuOverlay.classList.remove('active');
					}
				}
			}
			return;
		}

		// Handle top-level menu button clicks (ONLY for hamburger menu)
		const topLevelLink = target.closest('.top-level-link.has-children');
		if (topLevelLink && topLevelLink.classList.contains('hamburger-menu-item')) {
			const id = topLevelLink.getAttribute('data-menu-id');
			if (id) {
				const associatedMegaMenu = document.querySelector(`.mega-menu-dropdown[data-menu-id="${id}"]`);
				const megaMenuOverlay = document.querySelector(`.mega-menu-overlay`);
				if (associatedMegaMenu) {
					const isCurrentlyActive = associatedMegaMenu.classList.contains('active');

					// Close all mega menus first
					document.querySelectorAll('.mega-menu-dropdown.active').forEach((menu) => {
						menu.classList.remove('active');
					});
					document.querySelectorAll('.top-level-link.active').forEach((link) => {
						link.classList.remove('active');
					});

					// If it wasn't active, open it. If it was active, leave it closed (toggle behavior)
					if (!isCurrentlyActive) {
						topLevelLink.classList.add('active');
						associatedMegaMenu.classList.add('active');
					}

					// Toggle overlay based on whether any menu is now open
					if (megaMenuOverlay) {
						const anyMenuOpen = document.querySelector('.mega-menu-dropdown.active');
						if (anyMenuOpen) {
							megaMenuOverlay.classList.add('active');
						} else {
							megaMenuOverlay.classList.remove('active');
						}
					}
				}
			}
			return;
		}

		// Check if clicking on items INSIDE the dropdown menu (not the top-level buttons)
		const megaMenuNav = document.querySelector('.mega-menu-dropdown-left .sublinks-container');
		if (megaMenuNav && target.closest('.sublinks-container')) {
			// Look for any element with data-menu-item attribute (could be parent-link or fade-in)
			let menuItem = target.closest('[data-menu-item]');
			if (menuItem) {
				// Handle submenu toggle for items with children
				const submenu = menuItem.querySelector('.submenu');
				if (submenu) {
					menuItem.classList.toggle('active');
					submenu.classList.toggle('active');
					megaMenuNav.classList.toggle('submenu-active');
				}

				// Show/hide blocks based on menu item
				this.showBlocksForMenuItem(menuItem);
			}
			return;
		}
	}

	showBlocksForMenuItem(menuItem) {
		const menuItemName = menuItem.getAttribute('data-menu-item');
		// console.log('showBlocksForMenuItem called for:', menuItemName);
		if (!menuItemName) return;

		const megaMenuDropdown = menuItem.closest('.mega-menu-dropdown');
		if (!megaMenuDropdown) {
			// console.log('No megaMenuDropdown found');
			return;
		}

		// Remove active class from all menu items
		const allMenuItems = megaMenuDropdown.querySelectorAll('[data-menu-item]');
		allMenuItems.forEach((item) => {
			item.classList.remove('active');
		});

		// Add active class to current menu item
		menuItem.classList.add('active');

		const blockLinksWrapper = megaMenuDropdown.querySelector('.block-links-wrapper');
		if (!blockLinksWrapper) {
			// console.log('No blockLinksWrapper found');
			return;
		}

		const blockContainers = blockLinksWrapper.querySelectorAll('.menu-item-blocks-container');
		// console.log('Found containers:', blockContainers.length);
		blockContainers.forEach((container) => {
			const forMenuItem = container.getAttribute('data-for-menu-item');
			const hasChildren = container.children.length > 0;
			// console.log('Container for:', forMenuItem, 'matches:', forMenuItem === menuItemName, 'hasChildren:', hasChildren, 'children:', container.children.length);
			if (forMenuItem === menuItemName) {
				container.classList.add('active');
				if (hasChildren) {
					// console.log('Activated container with content');
				} else {
					// console.log('WARNING: Activated empty container!');
				}
			} else {
				container.classList.remove('active');
			}
		});
	}

	mobileSubmenu(e) {
		const mobileMenuNav = document.querySelector('.mobile-menu-navigation');
		const menuLinks = document.querySelectorAll('.mobile-menu-navigation .link.has-children');

		if (menuLinks.length > 0) {
			const target = e.target.closest('.link.has-children');

			if (target) {
				const submenu = target.querySelector('.submenu');
				if (submenu) {
					target.classList.toggle('active');
					submenu.classList.toggle('active');
					mobileMenuNav.classList.toggle('submenu-active');
				}
			}
		}
	}

	headerSearch(e) {
		const search = document.querySelector('.header-search');
		const searchTrigger = document.querySelector('.mega-menu .mega-menu-controls [data-open-search]');
		const searchInput = document.querySelector('#header-search');

		if (search) {
			const target = e.target;

			if (target === searchTrigger || target.closest('[data-open-search]')) {
				searchTrigger.classList.toggle('active');
				search.classList.toggle('active');
				if (searchInput) {
					// Use requestAnimationFrame to ensure DOM is updated before focusing
					requestAnimationFrame(() => {
						setTimeout(() => {
							searchInput.focus();
						}, 50);
					});
				}
			} else if (target === searchInput) {
				// Do nothing if the search input is clicked
				return;
			} else {
				searchTrigger.classList.remove('active');
				search.classList.remove('active');
			}

			if (e.key === 'Escape') {
				if (searchTrigger.classList.contains('active')) {
					searchTrigger.classList.remove('active');
					search.classList.remove('active');
				}
			}
		}
	}

	mobileMenu(e) {
		const menu = document.querySelector('.mobile-menu-outer-wrapper');
		const menuOpen = document.querySelector('#open-mobile-menu');
		const menuClose = document.querySelector('#close-mobile-menu');

		if (menuOpen && e.target.closest('#open-mobile-menu')) {
			menu.classList.add('active');
			menuOpen.classList.add('active');
		}

		if (menuClose && e.target.closest('#close-mobile-menu')) {
			menu.classList.remove('active');
			menuOpen.classList.remove('active');
		}
	}

	accordions(e) {
		if (e.target.classList.contains(this.selectors.accordion)) {
			e.preventDefault();

			const accordion = e.target;
			const parent = accordion.parentElement;
			const sibling_accordions = parent.getElementsByClassName(this.selectors.accordion);
			const panel = accordion.nextElementSibling;


			if (sibling_accordions) {
				Array.from(sibling_accordions).forEach((acc) => {
					const acc_panel = acc.nextElementSibling;
					if (acc != accordion || accordion.classList.contains('active')) {
						if (acc_panel) {
							if (acc_panel.style.maxHeight === 'max-content' || (acc.classList.contains('active') && acc_panel.style.maxHeight !== '0px' && acc_panel.style.maxHeight !== '')) {
								acc_panel.style.maxHeight = acc_panel.scrollHeight + "px";
								acc_panel.offsetHeight; // Force layout reflow before transitioning to 0
							}
							acc_panel.style.maxHeight = null;
						}
						acc.classList.remove("active");
					} else {
						accordion.classList.add("active");
						panel.style.maxHeight = panel.scrollHeight + "px";
					}
				});
			}

		}
	}

	footerMobile(e) {
		const footerTitle = e.target.closest(this.selectors.footerColumnTitles);
		if (footerTitle) {
			e.preventDefault();
			const footerLinks = footerTitle.nextElementSibling;
			footerTitle.classList.toggle('active');
			if (footerLinks && footerLinks.classList.contains('links')) {
				footerLinks.classList.toggle("active");
			}
		}
	}

	initQuickViewCardListeners() {
		const quickViewCards = document.querySelectorAll(this.selectors.quickViewCard);
		quickViewCards.forEach(card => {
			card.addEventListener('click', this.openQuickViewCard.bind(this));
		});

		const quickviewCardsClose = document.querySelectorAll(this.selectors.quickviewCardClose);
		quickviewCardsClose.forEach(close => {
			close.addEventListener('click', this.closeQuickViewCard.bind(this));
		});
	}

	openQuickViewCard(e) {
		const quickViewCard = e.target.closest(this.selectors.quickViewCard);

		document.querySelectorAll(this.selectors.quickviewCardModal).forEach(quickViewRemove => {
			if (quickViewRemove.classList.contains('active')) {
				quickViewRemove.closest(this.selectors.productCard).classList.remove('qv-active');
				quickViewRemove.classList.remove('active');
				quickViewRemove.querySelector('.inner').innerHTML = ""
			}
		});

		if (quickViewCard) {
			e.preventDefault();

			setTimeout(async () => {
				// Find the closest modal to the clicked product card
				const closestProductCard = quickViewCard.closest(this.selectors.productCard);
				if (closestProductCard) {
					closestProductCard.classList.add('qv-active');
				}

				const quickviewCardModal = quickViewCard.closest(this.selectors.productCard).querySelector(this.selectors.quickviewCardModal);
				if (!quickviewCardModal) {
					console.error('quickviewCardModal not found');
					return;
				}

				let quickViewCardModalInner = quickviewCardModal.querySelector(".inner");
				if (!quickViewCardModalInner) {
					console.error('quickViewCardModalInner not found');
					return;
				}

				let sectionName = "hk-product-qv-card";
				let handle = quickViewCard.getAttribute("data-product-handle");
				if (handle) {
					try {
						const response = await fetch(window.Shopify.routes.root + `products/${handle}?sections=${sectionName}`);
						if (response.ok) {
							const data = await response.json();
							let html = data[`${sectionName}`];

							const scripts_to_run = html.match(/<script qv-run>([\s\S]*?)<\/script>/gm) || [];
							const scripts = [];
							scripts_to_run.forEach(script => {
								script = script.replace("</script>", "").replace("<script qv-run>", "");
								script = script.replace('document.addEventListener("DOMContentLoaded", () => {', "");

								let closingBraceLocation = script.lastIndexOf("});");
								script = script.substring(0, closingBraceLocation);

								scripts.push(script);
							});
							if (html) {
								quickViewCardModalInner.innerHTML = html;

								scripts.forEach(script_to_eval => {
									new Function(script_to_eval)();
								});

								quickviewCardModal.classList.add("active");

								//this.initDynamicContentListeners();
								this.productForm(e);
							}
						} else {
							throw new Error('Error: ' + response.status);
						}
					} catch (error) {
						console.error(error);
					}
				}
			}, 0);
		}
	}

	closeQuickViewCard(e) {
		const quickviewCardClose = e.target.closest(this.selectors.quickviewCardClose);

		if (quickviewCardClose) {
			e.preventDefault();

			const closestProductCard = quickviewCardClose.closest(this.selectors.productCard);
			if (closestProductCard) {
				closestProductCard.classList.remove('qv-active');
				closestProductCard.querySelector('.inner').innerHTML = ""
			}

			const quickviewCardModal = quickviewCardClose.closest(this.selectors.quickviewCardModal);
			if (quickviewCardModal) {
				quickviewCardModal.classList.remove("active");
			}
		}
	}

	initProductCardSwipers() {
		const productCardSwipers = document.querySelectorAll(this.selectors.productCardSwiper);
		productCardSwipers.forEach((swiperContainer) => {
			new window.swiper(swiperContainer, {
				// Swiper settings
				loop: true,
				pagination: {
					el: `.swiper-pagination`,
					clickable: true,
					dynamicBullets: true,
					dynamicMainBullets: 5
				},
				lazy: true,
				navigation: {
					nextEl: '.swiper-button-next',
					prevEl: '.swiper-button-prev',
				},
			});
		});
	}

	handleProductTitleBadges() {
		const badges = document.querySelector(this.selectors.productTitleBadges);

		if (badges) {
			const badgeWidth = badges.offsetWidth;
			badges.style.right = `-${badgeWidth + 15}px`;
			badges.classList.add('visible');
		}
	}
}